import React from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Button,
    useColorModeValue,
    Icon,
} from '@chakra-ui/react';
import { MdBlock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedPageProps {
    userEmail?: string;
    message?: string;
    onRetry?: () => void;
}

/**
 * Komponen untuk menampilkan halaman access denied
 * Ditampilkan ketika user tidak memiliki akses admin
 */
const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({
    userEmail,
    message = "Anda tidak memiliki akses ke halaman admin",
    onRetry,
}) => {
    const navigate = useNavigate();
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');

    const handleGoBack = () => {
        navigate('/');
    };

    return (
        <Box
            minH="100vh"
            bg={bgColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={4}
        >
            <Box
                maxW="md"
                w="full"
                bg={cardBg}
                boxShadow="xl"
                rounded="lg"
                p={8}
                textAlign="center"
            >
                <VStack spacing={6}>
                    {/* Icon */}
                    <Box>
                        <Icon
                            as={MdBlock}
                            w={16}
                            h={16}
                            color="red.500"
                        />
                    </Box>

                    {/* Heading */}
                    <Heading
                        as="h1"
                        size="xl"
                        color="red.600"
                    >
                        Akses Ditolak
                    </Heading>

                    {/* Alert */}
                    <Alert status="error" rounded="md">
                        <AlertIcon />
                        <Box>
                            <AlertTitle>Admin Access Required!</AlertTitle>
                            <AlertDescription>
                                {message}
                            </AlertDescription>
                        </Box>
                    </Alert>

                    {/* User Email Info */}
                    {userEmail && (
                        <Box>
                            <Text fontSize="sm" color="gray.600">
                                Logged in as:
                            </Text>
                            <Text fontWeight="semibold" color="gray.800">
                                {userEmail}
                            </Text>
                        </Box>
                    )}

                    {/* Description */}
                    <Text color="gray.600" textAlign="center">
                        Halaman ini hanya dapat diakses oleh administrator. Silakan hubungi administrator
                        jika Anda memerlukan akses.
                    </Text>

                    {/* Action Buttons */}
                    <VStack spacing={3} w="full">
                        {onRetry && (
                            <Button
                                colorScheme="blue"
                                variant="outline"
                                onClick={onRetry}
                                w="full"
                            >
                                Coba Lagi
                            </Button>
                        )}

                        <Button
                            colorScheme="gray"
                            onClick={handleGoBack}
                            w="full"
                        >
                            Kembali ke Dashboard
                        </Button>
                    </VStack>

                    {/* Footer */}
                    <Text fontSize="xs" color="gray.500" mt={4}>
                        Jika Anda yakin ini adalah kesalahan, silakan hubungi tim IT.
                    </Text>
                </VStack>
            </Box>
        </Box>
    );
};

export default AccessDeniedPage;
